/**
 * ThemeContext — Dark mode / light mode / system-follow.
 *
 * Reads user choice from localStorage (`r237-theme`) or falls back to
 * `prefers-color-scheme`. Applies the `dark` class to <html> so
 * Tailwind's `dark:` variants take effect.
 *
 * IMPORTANT: The initial theme is ALREADY applied in a small inline
 * <script> in public/index.html BEFORE React mounts. This context is
 * only responsible for the runtime toggle (user clicks the button).
 * Without the inline pre-hydration script, the page would briefly flash
 * the wrong theme (FOUC).
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'r237-theme';
const ThemeContext = createContext({
  theme: 'system',       // 'light' | 'dark' | 'system'
  resolved: 'light',     // 'light' | 'dark' — what actually applies right now
  setTheme: () => {},
  toggle: () => {},
});

function resolveTheme(theme) {
  if (theme === 'system') {
    return typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark' : 'light';
  }
  return theme;
}

function applyTheme(resolved) {
  const root = document.documentElement;
  if (resolved === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  // Also update the theme-color meta so the mobile status bar matches.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#1c1917' : '#ea580c');
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    } catch { /* localStorage may be blocked (Safari private) */ }
    return 'system';
  });
  const [resolved, setResolved] = useState(() => resolveTheme(theme));

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
  }, []);

  const toggle = useCallback(() => {
    // Toggle cycles light → dark → system → light
    setTheme((prev) => {
      const current = typeof prev === 'string' ? prev : theme;
      if (current === 'light') return 'dark';
      if (current === 'dark')  return 'system';
      return 'light';
    });
  }, [setTheme, theme]);

  // Recompute resolved theme + apply when user choice or system pref changes.
  useEffect(() => {
    const next = resolveTheme(theme);
    setResolved(next);
    applyTheme(next);

    if (theme !== 'system') return undefined;
    // Listen for system pref changes while user is on "system" mode
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => {
      const r = e.matches ? 'dark' : 'light';
      setResolved(r);
      applyTheme(r);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme, toggle }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
